import {
  Alert,
  Amount,
  Box,
  Button,
  CheckCircle2Icon,
  Heading,
  Text,
} from '@razorpay/blade/components';
import React from 'react';
import { useDraftEmployeeData } from './hooks/useDraftEmployeeData';
import { makeCompensationBreakDownData, updateInitialCompensationBreakDownFormData } from './utils';
import { useDraftEmployeeId } from './hooks/useDraftEmployeeId';
import { initialCompensationBreakdownData } from './data';
import { CompensationBreakdownFormFields } from './types';
import { useForm } from 'forms';
import { useQuery } from 'react-query';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import api from 'api';
import { parseGetSalaryStructureResponse } from 'components/SalaryStructure/utils';
import { format } from 'date-fns';
import { FloatingDiscardButton } from '../components/FloatingDiscardButton';

import { CTCPreview } from './components/CTCBreakdown/CTCPreview';

import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { FormFieldsWrapper, FormFooterWrapper, StyledForm } from '../components/styles';

const getSalaryData = ({
  heading,
  value,
  isAmount = false,
}: {
  heading: string;
  value: string;
  isAmount?: boolean;
}) => {
  return (
    <Box
      display="grid"
      gridTemplateColumns={'2fr 1fr'}
      gap="spacing.11"
      alignItems={'center'}
      maxWidth={'300px'}>
      <Box display="flex" gap="spacing.3" alignItems={'center'}>
        <CheckCircle2Icon color="feedback.icon.positive.intense" size="small" />
        <Text size="small" weight="semibold" color="surface.text.gray.muted">
          {heading}
        </Text>
      </Box>
      {isAmount ? (
        <Amount value={Number(value) || 0} />
      ) : (
        <Text size="small" weight="semibold" color="surface.text.gray.subtle">
          {value}
        </Text>
      )}
    </Box>
  );
};

export const CompensationBreakdown = ({
  setShowDiscardModal,
}: {
  setShowDiscardModal: (value: boolean) => void;
}) => {
  const { draftEmployeeId } = useDraftEmployeeId();
  const history = useHistory();
  const { setFormData, formData } = useForm<CompensationBreakdownFormFields>(
    initialCompensationBreakdownData,
  );

  const { isLoading, isFetching } = useDraftEmployeeData({
    onSuccess: (data) => updateInitialCompensationBreakDownFormData(data, setFormData),
  });

  const { salaryStructureId, annualCtc, ctcBreakdownData } = formData;
  const {
    data: salaryStructureData,
    isFetching: isFetchingSalaryStructureData,
    isError: salaryStructureError,
    isLoading: isLoadingSalaryStructureData,
    refetch: refetchSalaryStructureData,
  } = useQuery({
    queryKey: [salaryStructureQueries.getSalaryStructure, salaryStructureId?.[0]],
    queryFn: async () => {
      const result = await api.salaryStructures.queries.getSalaryStructure({
        input: {
          id: salaryStructureId?.[0] || '',
        },
      });

      const response = parseGetSalaryStructureResponse(result);

      return response.payrollGetSalaryStructure.data;
    },
    enabled: !!salaryStructureId?.[0] && !isLoading && !isFetching,
  });

  const {
    isFetching: isFetchingCompensationData,
    isLoading: isLoadingCompensationData,
    isError: compensationError,
    refetch: refetchCompensationData,
  } = useQuery({
    queryKey: [salaryStructureQueries.getSalaryComponentSDIs, salaryStructureId?.[0]],
    queryFn: async () => {
      const parsedSalaryComponents = salaryStructureData?.salaryStructureComponents;

      const inputs = parsedSalaryComponents?.map(
        ({ salaryComponentId, formula, valueType, salaryComponent }) => {
          return {
            salaryComponentId,
            formula:
              !!formula || valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_SA' ? formula : '0',
            valueType,
          };
        },
      );

      const result = await api.salaryStructures.queries.getSalaryComponentSDIs({
        input: {
          requests: inputs || [],
          compensation: annualCtc || '0',
          payrollMonth: format(Date.now(), 'yyyy-MM'),
        },
      });

      return result.payrollGetSalaryComponentSDIs.data;
    },
    onSuccess: (data) => {
      const earnings = data.payrolls[0].data;
      const salaryStructureComponents = salaryStructureData?.salaryStructureComponents || [];

      setFormData({
        ...formData,
        ctcBreakdownData: makeCompensationBreakDownData(earnings, salaryStructureComponents),
      });
    },
    enabled:
      !isFetching &&
      !isFetchingSalaryStructureData &&
      !salaryStructureError &&
      !!salaryStructureData &&
      !isLoading &&
      !isLoadingSalaryStructureData,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    history.push({
      pathname: routePaths.employeeOnboarding.employee.compliance,
      search: `?draftId=${draftEmployeeId}`,
    });
  };

  const handleStepBack = () => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.compensation,
      search: `?draftId=${draftEmployeeId}`,
    });
  };

  const isLoadingApiData = isLoading || isLoadingCompensationData || isLoadingSalaryStructureData;
  const isFetchingApiData =
    isFetching || isFetchingCompensationData || isFetchingSalaryStructureData;

  const shouldDisableButtons = isLoadingApiData || isFetchingApiData;

  return (
    <>
      <FloatingDiscardButton
        isDisabled={shouldDisableButtons}
        handleDiscardModal={() => setShowDiscardModal(true)}
      />

      {salaryStructureError ? (
        <Box
          display={'flex'}
          flexDirection={'column'}
          gap={'spacing.4'}
          marginTop={'spacing.11'}
          marginX={'auto'}
          width={'80%'}>
          <Alert
            description={'This page could not be loaded due to unknown error.'}
            color="negative"
            isFullWidth
            isDismissible={false}
            testID="salary-structure-error-alert"
            actions={{
              primary: {
                text: 'Retry',
                onClick: refetchSalaryStructureData,
              },
            }}
          />
        </Box>
      ) : (
        <StyledForm onSubmit={handleSubmit}>
          <FormFieldsWrapper shouldShowSpinner={isFetchingApiData}>
            <Box>
              <Heading size="large" weight="regular" textAlign="left">
                Compensation Breakdown
              </Heading>
              <Box marginTop="spacing.6" display="flex" flexDirection="column" gap={'spacing.4'}>
                {getSalaryData({
                  heading: 'Salary Structure',
                  value: salaryStructureData?.name || '',
                })}
                {getSalaryData({ heading: 'Annual CTC', value: annualCtc || '', isAmount: true })}
              </Box>
              <Box marginTop="spacing.9">
                <CTCPreview
                  isLoadingOrFetching={isFetchingApiData}
                  ctcData={ctcBreakdownData}
                  compensationError={compensationError}
                  refetchCompensationData={refetchCompensationData}
                />
              </Box>
            </Box>
          </FormFieldsWrapper>
          <FormFooterWrapper maxWidth="600px">
            <Button variant="secondary" onClick={handleStepBack} isDisabled={isLoading}>
              Back
            </Button>
            <Box display="flex" gap="spacing.3">
              <Button isDisabled={shouldDisableButtons} type="submit" isLoading={isLoadingApiData}>
                Confirm
              </Button>
            </Box>
          </FormFooterWrapper>
        </StyledForm>
      )}
    </>
  );
};
